import { EXTERNAL_SCOPE_TYPES } from '../models/messaging-user';
import {
  conversationUserNames,
  firstNameAndLastNameInitial,
} from '../utils/messaging/conversation';
import { decodeText } from '../utils/messaging/format-text';
import { filterSystemAndCurrentUser } from '../utils/messaging/user';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import AttachTooltip from 'ember-attacher/components/attach-tooltip';
import Avatar from './avatar.gjs';
import Component from '@glimmer/component';
import FormatMessagingTimestamp from '../helpers/format-messaging-timestamp';
import styles from './conversation-list-item.module.scss';

export default class ConversationListItem extends Component {
  @service messaging;
  @service messagingNavigation;
  @service intl;

  get isUnread() {
    return this.messaging.currentProfile.unreadConversationIds.includes(this.args.conversation.id);
  }

  get trimmedDisplayName() {
    return this.args.conversation.displayName?.trim() || '';
  }

  get hasDisplayName() {
    return Boolean(this.trimmedDisplayName);
  }

  get userNames() {
    const names = this.sortedFilteredUsers.map(user =>
      this.args.conversation.isGroup ? firstNameAndLastNameInitial(user.name) : user.name
    );
    return conversationUserNames(names);
  }

  get conversationTitle() {
    return this.hasDisplayName ? this.trimmedDisplayName : this.userNames;
  }

  get userNamesTooltip() {
    const fullNames = this.sortedFilteredUsers.map(user => user.name);
    return conversationUserNames(fullNames);
  }

  get showUserNamesTooltip() {
    return this.hasDisplayName || this.args.conversation.isGroup || this.args.conversation.isCouple;
  }

  get emptyConversationPlaceholder() {
    return this.intl.t('emc.start_conversation_callout', {
      name: this.userNames,
    });
  }

  get filteredUsers() {
    const currentUser = this.messaging.currentProfile?.user;
    const conversation = this.args.conversation;
    const members = conversation.currentMembers?.(currentUser) ?? conversation.users;

    return filterSystemAndCurrentUser(members, currentUser);
  }

  get sortedFilteredUsers() {
    const users = this.filteredUsers ?? [];
    return [...users].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }

  get clinicianUser() {
    return this.filteredUsers.find(user => user.externalScope === EXTERNAL_SCOPE_TYPES.user);
  }

  get showAwayIndicator() {
    return (
      this.messaging.currentProfile?.isClient &&
      !this.args.conversation.isGroup &&
      this.clinicianUser?.isAway
    );
  }

  get showLastMessageAuthor() {
    return this.args.conversation.isGroup || this.args.conversation.isCouple;
  }

  get lastMessageAuthorPrefix() {
    const authorName = this.args.conversation.lastMessage?.user?.name;
    if (!this.showLastMessageAuthor || !authorName) return '';
    return `${firstNameAndLastNameInitial(authorName)}: `;
  }

  openConversation = () => {
    this.messagingNavigation.toConversation(this.args.conversation);
  };

  <template>
    <li class="conversation-list-item" data-test-conversation={{@conversation.id}}>
      <button type="button" class={{styles.component}} {{on "click" this.openConversation}}>
        <Avatar
          @users={{this.sortedFilteredUsers}}
          @isGroup={{@conversation.isGroup}}
          @isCouple={{@conversation.isCouple}}
        />

        <div class="item-details">
          <div class="top-line">
            {{#if this.isUnread}}
              <span
                class="unread-dot"
                aria-label={{t "emc.unread_message_indicator"}}
                data-test-unread-indicator
              ></span>
            {{/if}}
            <span class="user-names" data-test-user-names>
              {{this.conversationTitle}}
              {{#if this.showUserNamesTooltip}}
                <AttachTooltip
                  @placement="bottom"
                  @arrow={{true}}
                  @animation="fade"
                  @class="emc-tooltip"
                >
                  <span data-test-user-names-tooltip>{{this.userNamesTooltip}}</span>
                </AttachTooltip>
              {{/if}}
              {{#if this.showAwayIndicator}}
                <span class="away-indicator" data-test-away-indicator>
                  {{t "emc.away_status.indicator"}}
                </span>
              {{/if}}
            </span>
            <span class="timestamp" data-test-timestamp>
              {{FormatMessagingTimestamp @conversation.updatedAt}}
            </span>
          </div>
          <p class="last-message" data-test-last-message>
            {{#if @conversation.hasMessageDraft}}
              <span class="draft-indicator" data-test-draft-indicator>
                {{t "emc.draft_indicator"}}
              </span>
              {{decodeText @conversation.messageDraft}}
            {{else if @conversation.lastMessage}}
              {{this.lastMessageAuthorPrefix}}{{decodeText @conversation.lastMessage.text}}
            {{else}}
              <span class="empty-conversation-callout">
                {{this.emptyConversationPlaceholder}}
              </span>
            {{/if}}
          </p>
        </div>
      </button>
    </li>
  </template>
}
